/**
=========================================================
* TripEnhancer - Article QR Code Page
=========================================================
* 
* Displays a printable QR code for an article with
* Tourist Information styling
*/

import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { QRCodeSVG } from "qrcode.react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Article data
import { getArticleData } from "lib/articles";

function ArticleQRCode() {
  const { articleId } = useParams();
  const article = getArticleData(articleId);
  const articleUrl = `https://tripenhancer.app/article/${articleId}`;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadSVG = () => {
    const svg = document.getElementById("qr-code-svg");
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const downloadLink = document.createElement("a");
    downloadLink.href = svgUrl;
    downloadLink.download = `tripenhancer-${articleId}-qr.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <>
      {/* Print Styles */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .print-page {
              page-break-after: always;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
            }
          }
        `}
      </style>

      {/* Screen View - Controls */}
      <MKBox className="no-print" sx={{ py: 6, minHeight: "100vh", bgcolor: "#f8f9fa" }}>
        <Container>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card sx={{ p: 4 }}>
                <MKBox textAlign="center" mb={3}>
                  <MKTypography variant="h3" mb={1}>
                    QR Code Generator
                  </MKTypography>
                  <MKTypography variant="body2" color="text">
                    Printable QR code for {article.title}
                  </MKTypography>
                </MKBox>

                {/* Action Buttons */}
                <MKBox display="flex" justifyContent="center" gap={2} mb={4}>
                  <MKButton variant="gradient" color="info" onClick={handlePrint}>
                    <Icon sx={{ mr: 1 }}>print</Icon>
                    Print QR Code
                  </MKButton>
                  <MKButton variant="outlined" color="info" onClick={handleDownloadSVG}>
                    <Icon sx={{ mr: 1 }}>download</Icon>
                    Download SVG
                  </MKButton>
                </MKBox>

                {/* Preview */}
                <MKBox
                  sx={{
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    p: 4,
                    bgcolor: "white",
                  }}
                >
                  <PrintableQRCard article={article} articleUrl={articleUrl} />
                </MKBox>

                {/* Instructions */}
                <MKBox mt={4} p={3} sx={{ bgcolor: "#e3f2fd", borderRadius: 2 }}>
                  <MKTypography variant="h6" mb={2}>
                    📋 How to Use
                  </MKTypography>
                  <MKTypography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        <strong>Print:</strong> Click &quot;Print QR Code&quot; to print the sticker
                      </li>
                      <li>
                        <strong>Download:</strong> Click &quot;Download SVG&quot; for high-quality
                        graphics
                      </li>
                      <li>
                        <strong>Scan:</strong> Visitors can scan the QR code to open the article
                      </li>
                      <li>
                        <strong>Size:</strong> The QR code is optimized for A4/Letter printing
                      </li>
                    </ul>
                  </MKTypography>
                </MKBox>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      {/* Print View */}
      <MKBox className="print-page" sx={{ display: "none" }}>
        <PrintableQRCard article={article} articleUrl={articleUrl} />
      </MKBox>
    </>
  );
}

// Printable QR Card Component
function PrintableQRCard({ article, articleUrl }) {
  return (
    <MKBox
      sx={{
        width: "100%",
        maxWidth: "400px",
        margin: "0 auto",
        textAlign: "center",
        bgcolor: "white",
        p: 4,
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
      }}
    >
      {/* Tourist Information Icon */}
      <MKBox
        sx={{
          width: "80px",
          height: "80px",
          margin: "0 auto 20px",
          borderRadius: "50%",
          bgcolor: "#1976d2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(25,118,210,0.3)",
        }}
      >
        <MKTypography
          variant="h1"
          sx={{
            color: "white",
            fontWeight: "bold",
            fontSize: "48px",
            fontFamily: "serif",
            fontStyle: "italic",
          }}
        >
          i
        </MKTypography>
      </MKBox>

      {/* Title */}
      <MKTypography variant="h4" mb={1} sx={{ fontWeight: "bold", color: "#1976d2" }}>
        {article.title}
      </MKTypography>
      <MKTypography variant="body2" mb={3} color="text">
        {article.location.city}
      </MKTypography>

      {/* QR Code */}
      <MKBox
        sx={{
          p: 3,
          bgcolor: "white",
          borderRadius: 2,
          border: "3px solid #1976d2",
          display: "inline-block",
          mb: 3,
        }}
      >
        <QRCodeSVG
          id="qr-code-svg"
          value={articleUrl}
          size={256}
          level="H"
          includeMargin={false}
          fgColor="#1976d2"
        />
      </MKBox>

      {/* URL */}
      <MKTypography
        variant="caption"
        sx={{
          display: "block",
          color: "#666",
          fontSize: "10px",
          wordBreak: "break-all",
          mb: 2,
        }}
      >
        {articleUrl}
      </MKTypography>

      {/* Instructions */}
      <MKBox
        sx={{
          p: 2,
          bgcolor: "#f5f5f5",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
        }}
      >
        <MKTypography variant="body2" sx={{ fontSize: "12px", color: "#666" }}>
          📱 <strong>Scan to learn more</strong>
          <br />
          Open your camera and point at the QR code
        </MKTypography>
      </MKBox>

      {/* Branding */}
      <MKTypography
        variant="caption"
        sx={{
          display: "block",
          mt: 3,
          color: "#999",
          fontSize: "11px",
          fontWeight: "bold",
        }}
      >
        Powered by TripEnhancer
      </MKTypography>
    </MKBox>
  );
}

PrintableQRCard.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string.isRequired,
    location: PropTypes.shape({
      city: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  articleUrl: PropTypes.string.isRequired,
};

export default ArticleQRCode;
